import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { RootState } from "../state";
import {
  login as loginCall,
  refreshToken as refreshTokenCall,
  loginSocial as loginSocialCall,
  register as registerCall,
  forgotPassword as forgotPasswordCall,
  recoveryPassword as recoveryPasswordCall,
} from "../services/api/authService";
import {
  setLogin as setLoginSlice,
  cleanLogin as cleanLoginSlice,
} from "../components/Login/loginSlice";
import { setLoginData, removeLoginData } from "./LocalStorage/loginDataLS";
import NotificationService from "../components/Notification/notificationService";
import { LoginData } from "../types/authTypes";
import { User } from "../entities/User";
import DialogService from "../components/Dialog/dialogService";
import { useNavigate } from "react-router-dom";
import settings from "../config/settings";
import BackdropLoaderService from "../components/BackdropLoader/backdropLoaderService";
import RedirectService from "./redirectService";
import { COLLECTION_TAB } from "../pages/MyProfile/MyProfile";
import { COLLECTION_SECTION } from "../pages/MyProfile/Collection/Collection";
import API from "./api/api";

export default function LoginService() {
  const dispatch = useDispatch();

  const { error: errorNotification } = NotificationService();
  const { clearDialogs } = DialogService();
  const { close: closeBDL } = BackdropLoaderService();
  const navigate = useNavigate();

  const loginSlice = useSelector((state: RootState) => state.login);

  const handleError = (status: number, description: string) => {
    errorNotification(description);
  };

  const cleanLogin = () => {
    dispatch(cleanLoginSlice());
  };

  const register = async (
    username: string,
    email: string,
    password: string,
    password_confirmation: string
  ) => {
    let response = await registerCall(
      username,
      email,
      password,
      password_confirmation
    ).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    if (response !== undefined && response.error === undefined) {
      return true;
    } else {
      return false;
    }
  };

  const forgotPassword = async (email: string) => {
    let response = await forgotPasswordCall(email).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    if (response !== undefined && response.error === undefined) {
      return true;
    } else {
      return false;
    }
  };

  const recoveryPassword = async (
    token: string,
    password: string,
    password_confirmation: string
  ) => {
    let response = await recoveryPasswordCall(
      token,
      password,
      password_confirmation
    ).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    if (response !== undefined && response.error === undefined) {
      return true;
    } else {
      return false;
    }
  };

  const setLogin = async (email: string, password: string) => {
    let response = await loginCall(email, password).catch(function (error) {
      const status = error.response.status;
      const description = error.response.data.error_description;
      handleError(status, description);
    });

    if (response !== undefined && response.error === undefined) {
      storeLogin(response);
      return response;
    } else {
      return undefined;
    }
  };

  const setGoogleLogin = async (token: string) => {
    let response = await loginSocialCall("google", token).catch(
      function (error) {
        const status = error.response.status;
        const description = error.response.data.error_description;
        handleError(status, description);
      }
    );

    if (response !== undefined && response.error === undefined) {
      storeLogin(response);
      return response;
    } else {
      return undefined;
    }
  };

  const setLogout = () => {
    cleanLogin();
    removeLoginData();
    clearDialogs();
    closeBDL();
    navigate(settings.homePage);
  };

  const refreshToken = async () => {
    let loginDataLS = loginSlice;
    if (loginDataLS !== undefined && loginDataLS.refresh_token !== undefined) {
      let response = await refreshTokenCall(loginDataLS.refresh_token).catch(
        function (error) {
          const status = error.response.status;
          const description = error.response.data.error_description;
          handleError(status, description);
        }
      );
      if (response.error === undefined) {
        // Use a flag to prevent redirect when refreshing tokens
        storeLoginWithoutRedirect(response);
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  const storeLoginWithoutRedirect = (response: LoginData) => {
    const userData = new User(
      response.user?.id,
      response.access_token,
      response.user?.username,
      response.refresh_token,
      response.access_token_exp,
      response.refresh_token_exp,
      response.user?.role?.id,
      response.user?.email,
      response.user?.profile_image,
      response.user?.header_image,
      response.user?.userData,
      response.user?.collection,
      response.user ? response.user.social_networks : [],
      undefined
    );
    dispatch(setLoginSlice(userData));
    setLoginData(userData);
    // No redirect logic here - this is just for token refresh
  };

  const storeLogin = (response: LoginData) => {
    console.log("🔐 LoginService: Starting storeLogin process");
    const { getRedirectUrl, clearRedirectUrl } = RedirectService();

    // Set login in progress flag to prevent auto-logout during token propagation
    API.setLoginInProgress(true);
    console.log("🔐 LoginService: Set login in progress flag");

    const userData = new User(
      response.user?.id,
      response.access_token,
      response.user?.username,
      response.refresh_token,
      response.access_token_exp,
      response.refresh_token_exp,
      response.user?.role?.id,
      response.user?.email,
      response.user?.profile_image,
      response.user?.header_image,
      response.user?.userData,
      response.user?.collection,
      response.user ? response.user.social_networks : [],
      undefined
    );
    dispatch(setLoginSlice(userData));
    setLoginData(userData);
    console.log("🔐 LoginService: User data stored in Redux and localStorage");

    // Check for redirect URL after successful login
    const redirectUrl = getRedirectUrl();
    if (redirectUrl) {
      clearRedirectUrl();

      // Add a small delay to ensure Redux state has been updated
      setTimeout(() => {
        // Clear the login in progress flag before redirecting
        API.setLoginInProgress(false);
        console.log(
          "🔐 LoginService: Cleared login in progress flag, redirecting to:",
          redirectUrl
        );

        // Handle special case for My Collection redirect
        if (redirectUrl === "/my-collection-redirect") {
          const targetUrl =
            "/my-profile/" +
            response.user?.id +
            "/" +
            COLLECTION_TAB.COLLECTION +
            "/" +
            COLLECTION_SECTION.OVERVIEW;
          console.log(
            "🔐 LoginService: Redirecting to My Collection:",
            targetUrl
          );
          navigate(targetUrl);
        } else {
          console.log(
            "🔐 LoginService: Redirecting to stored URL:",
            redirectUrl
          );
          navigate(redirectUrl);
        }
      }, 100);
    } else {
      console.log("🔐 LoginService: No redirect URL found");
      // Clear the login in progress flag if no redirect
      setTimeout(() => {
        API.setLoginInProgress(false);
        console.log(
          "🔐 LoginService: Cleared login in progress flag (no redirect)"
        );
      }, 500);
    }
  };

  const storeUser = (user: User) => {
    dispatch(setLoginSlice(user));
    setLoginData(user);
  };

  const saveUser = (userData: User) => {
    dispatch(setLoginSlice(userData));
    setLoginData(userData);
  };

  return {
    refreshToken,
    setLogin,
    setLogout,
    saveUser,
    setGoogleLogin,
    register,
    forgotPassword,
    recoveryPassword,
    storeUser,
  };
}
